// ============================================================
// Inline tooltip stylesheet — TWS-122
// ------------------------------------------------------------
// The tooltip directive creates its host DOM imperatively (it
// lives on `document.body`, not inside any component template),
// so the standard Angular `styleUrls` pipeline does not apply.
// Instead, we ship the CSS as a TS string and inject it into
// `document.head` exactly once per page load — guarded by
// `styleInjected` in the directive file.
//
// ------------------------------------------------------------
// SYNC WITH `_tw-tooltip.scss`
// ------------------------------------------------------------
// This string must stay in lockstep with
// `libs/ui/popovers/src/lib/tooltip/_tw-tooltip.scss` — the SCSS
// file runs through the standard build for Storybook and design
// review, and this string actually renders at runtime. The two
// are kept in sync manually; when the tooltip's rendering path
// is refactored to use a component host with `styleUrls`
// (deferred — out of scope for this commit), this file is
// expected to go away entirely.
//
// Both sides currently include:
//   • The placement matrix (top/bottom/start/end) with the
//     opposite-edge `auto` clears required to neutralise the UA
//     `[popover]` `inset: 0` default — missing these is the bug
//     TWS-122 fixes for `placement='top'` and `placement='start'`.
//   • The spec-direct `anchor()` inset form (no `100% -`
//     subtraction — see shared mixin comment).
//
// Rules are wrapped in `@layer components` so they slot into the
// same cascade layer as the dropdown + popover CSS.
// ============================================================

/**
 * Plain-CSS tooltip stylesheet. Consumed by
 * `TwTooltipDirective.ensureStylesInjected()`.
 *
 * @internal
 */
export const TW_TOOLTIP_STYLESHEET = `
@layer components {
  .tw-tooltip {
    border: 0;
    margin: 0;
    inline-size: max-content;
    max-inline-size: 240px;
    padding: var(--twist-spacing-1) var(--twist-spacing-2);

    background-color: var(--twist-color-gray-950);
    color: var(--twist-color-white);
    border-radius: var(--twist-radius-sm);
    box-shadow: var(--twist-shadow-sm);

    font-size: var(--twist-font-size-body-sm);
    line-height: 1.3;
    text-align: start;

    z-index: 10000;
    pointer-events: none;
  }

  .tw-tooltip[data-placement='bottom'] {
    position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;
    top: calc(anchor(bottom) + var(--twist-spacing-1));
    bottom: auto;
    left: anchor(start);
    right: auto;
  }

  .tw-tooltip[data-placement='top'] {
    position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;
    top: auto;
    bottom: calc(anchor(top) + var(--twist-spacing-1));
    left: anchor(start);
    right: auto;
  }

  .tw-tooltip[data-placement='end'] {
    position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;
    inset-inline-start: calc(anchor(end) + var(--twist-spacing-1));
    inset-inline-end: auto;
    top: anchor(top);
    bottom: auto;
  }

  .tw-tooltip[data-placement='start'] {
    position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;
    inset-inline-start: auto;
    inset-inline-end: calc(anchor(start) + var(--twist-spacing-1));
    top: anchor(top);
    bottom: auto;
  }

  .tw-tooltip {
    transition: opacity var(--twist-duration-fast) var(--twist-easing-glass);
    opacity: 0;
  }

  .tw-tooltip:popover-open {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .tw-tooltip {
      transition: none;
    }
  }
}
`;
